import { useEffect, useRef } from 'react';
import { useWorldStore } from '../state/worldStore';
import { audioEngine } from '../services/audioEngine';
import { playheadOf } from '../services/listening';

/**
 * Everybody in the room hears what the host put on.
 *
 * Khari, 2026-08-04: participants "listen to the playlists and other shared
 * selections of the person who opened the room."
 *
 * THE WHOLE SYNC IS ARITHMETIC. The room stores `startedAt` — an epoch stamp of
 * when the record began — and each client works out its own position as
 * `now - startedAt` and seeks there. There is no heartbeat to miss, no stream to
 * relay, nothing to reconnect, and somebody sitting down halfway through hears
 * the middle of the song rather than the start of it. The host's tab going to
 * sleep does not stop the room either, because nothing was ever being pushed.
 *
 * It also costs nothing to run, which is the point: each client is playing a
 * track it can already fetch. Khari, 2026-08-04: "this would only cost voice
 * generation. everything else is free."
 *
 * ONE PLAY PER TRACK. Keyed on the track id AND its start stamp, so re-playing
 * the same record (the host putting it on again) counts as a new event while a
 * routine state push — somebody sitting down, a chair freeing — does not restart
 * the music under everyone.
 */
export default function useListeningPlayback() {
  const room = useWorldStore((s) => s.listeningRoom);
  const interior = useWorldStore((s) => s.interior);
  const lastRef = useRef('');

  useEffect(() => {
    const np = room?.nowPlaying;
    // Only while you are actually in the room it belongs to. Hearing a room you
    // walked out of is worse than silence.
    if (!np?.audioUrl || !room || room.zone !== interior) { lastRef.current = ''; return; }

    const key = `${np.trackId}:${np.startedAt}`;
    if (lastRef.current === key) return;

    const seconds = playheadOf(np);
    // Past the end and the host simply has not queued the next one — starting
    // it over would be worse than leaving the room quiet.
    if (np.durationSec && seconds > np.durationSec) {
      console.log(`[listening] "${np.title}" already finished (${Math.round(seconds)}s of ${np.durationSec}s) — waiting for the host`);
      lastRef.current = key;
      return;
    }

    lastRef.current = key;
    console.log(`[listening] joining "${np.title || np.trackId}" at ${Math.round(seconds)}s`);
    audioEngine.playTrack(
      { track_id: np.trackId, title: np.title, artist: np.artist, audio_url: np.audioUrl },
      { seekMs: Math.max(0, seconds * 1000) },
    );
  }, [room, interior]);
}
